import { useLingui } from '@lingui/react/macro'
import type { FimSubjectRecord } from 'features/fim/types'
import { useGeckoNotifications } from 'features/notifications'
import React, { useCallback } from 'react'

import { errorMessage } from './errorMessage'
import { PHOTO_PENDING_HINT } from './photoElections'
import { PhotoLightbox } from './PhotoLightbox'
import { usePhotoService } from './photoService'
import type { SubjectPhoto } from './types'

export const SubjectPhotoLightbox: React.FC<{
  siteId: string
  subject: FimSubjectRecord
  photo: SubjectPhoto
  canEdit: boolean
  onChanged: () => void
  onClose: () => void
}> = ({ siteId, subject, photo, canEdit, onChanged, onClose }) => {
  const { t, i18n } = useLingui()
  const photoService = usePhotoService()
  const { errorNotification } = useGeckoNotifications()
  const photoId = photo.entry.id

  const onSaveCaption = useCallback(
    async (caption: string) => {
      try {
        await photoService.setPhotoCaption(photoId, caption || null)
        onChanged()
      } catch (error) {
        const reason = errorMessage(error)
        errorNotification(t`Caption save failed: ${reason}`)
      }
    },
    [errorNotification, onChanged, photoId, photoService, t]
  )

  const onDetach = useCallback(async () => {
    try {
      await photoService.detachPhoto(photoId, { siteId, subject })
      onClose()
      onChanged()
    } catch (error) {
      const reason = errorMessage(error)
      errorNotification(t`Detach failed: ${reason}`)
    }
  }, [
    errorNotification,
    onChanged,
    onClose,
    photoId,
    photoService,
    siteId,
    subject,
    t,
  ])

  const onDelete = useCallback(async () => {
    try {
      await photoService.discardPhoto(photoId)
      onClose()
      onChanged()
    } catch (error) {
      const reason = errorMessage(error)
      errorNotification(t`Delete failed: ${reason}`)
    }
  }, [errorNotification, onChanged, onClose, photoId, photoService, t])

  return (
    <PhotoLightbox
      entry={photo.entry}
      pending={photo.pending}
      pendingNote={i18n._(PHOTO_PENDING_HINT)}
      imageUrl={photo.imageUrl}
      canEdit={canEdit}
      onSaveCaption={onSaveCaption}
      onDetach={onDetach}
      onDelete={onDelete}
      onClose={onClose}
    />
  )
}
